// How ONE source market enters Saudi Arabia: which entry rules reach its
// passport, and which published category that resolves to.
//
// Both live here, together, because they are one answer. Section 2.3 "Visa &
// entry" and GTM 4.2 "Visa" publish that answer for the same passport on the
// same product, and a second derivation in a second component is exactly how the
// two screens came to disagree about the same market before. The server calls
// this when it builds the 4.2 payload; the web calls it when 2.3 renders a
// selected country off the raw `/saudi/visa-entry` list.

/** The fields the selection reads. Both the server and web rule types satisfy it. */
export interface EntryRuleSelector {
  /**
   * The market this rule belongs to; null when the source states it irrespective
   * of nationality. OPTIONAL because the web type is — a frontend can run ahead
   * of a server that doesn't send the field. A missing value is read as null
   * (global), which is the safe direction: every rule the source states per
   * country carries an iso3, and today the only rule stated globally is the
   * substitute visa.
   */
  iso3?: string | null;
  appliesWhen: string;
  /**
   * Whether this rule is itself a ROUTE INTO the country — an eVisa or visa on
   * arrival the traveller can obtain by satisfying its conditions — rather than
   * a caveat on one (the pilgrimage restriction), an alternative document for a
   * passport that is already easy (the UK's ETA), or a change not yet in force
   * (a signed-but-pending exemption).
   *
   * Only a granting rule can lift a market out of `consular`, which is why the
   * distinction is stored rather than inferred from the rule key at each call
   * site. OPTIONAL for the same reason `iso3` is; absent reads as false, so a
   * payload that predates the field publishes the stricter category rather than
   * claiming a route nobody sent.
   */
  grantsEntry?: boolean | null;
}

/**
 * The rules that belong to one market, in the order they should be read.
 *
 * Country-scoped rules come first — a fact about THIS passport outranks one
 * about a document the traveller might happen to hold. A globally-stated rule
 * follows, and only on the markets its `appliesWhen` selects.
 */
export function rulesForMarket<R extends EntryRuleSelector>(
  rules: readonly R[],
  iso3: string,
  opts: { officialEligible: boolean | null | undefined; refused: boolean },
): R[] {
  // Refusal of entry sits outside visa facilitation — no route applies, so
  // offering one would contradict the verdict printed beside it.
  if (opts.refused) return [];
  const own = rules.filter((r) => (r.iso3 ?? null) === iso3);
  const global = rules.filter(
    (r) => (r.iso3 ?? null) === null && (r.appliesWhen === "all" || opts.officialEligible === false),
  );
  return [...own, ...global];
}

/** Does any rule reaching this market open a route in of its own? */
export function grantsConditionalRoute(rules: readonly EntryRuleSelector[]): boolean {
  return rules.some((r) => r.grantsEntry === true);
}

/**
 * Display precedence for the routes in, most broadly useful first.
 *
 * Section 2.3 publishes ONE of them — it is a lookup, answering "how does this
 * passport get in?" at a glance, and it was printing both routes under repeated
 * headings over five lines of small print. Which one it publishes must not
 * depend on the order Postgres happens to return: `saudi_entry_rules` has no
 * natural order, heap order reshuffles on any UPDATE, and "whichever came back
 * first" would have quietly swapped the published sentence between deploys —
 * the same instability `resolveVisaRows` exists to stop on the market rows.
 *
 * This is display order over keys THIS repo authors, not a claim about policy:
 * both routes are equally real, GTM 4.2 prints both in full with their
 * conditions, and a key missing from this list sorts last but still sorts.
 */
const ROUTE_DISPLAY_ORDER = ["substitute-visa", "residence-route"];

/** Routes into the Kingdom that reach this market, in publication order. */
export function conditionalRoutes<R extends EntryRuleSelector & { key?: string }>(rules: readonly R[]): R[] {
  const rank = (r: R) => {
    const i = ROUTE_DISPLAY_ORDER.indexOf(r.key ?? "");
    return i === -1 ? ROUTE_DISPLAY_ORDER.length : i;
  };
  return rules
    .filter((r) => r.grantsEntry === true)
    .sort((a, b) => rank(a) - rank(b) || (a.key ?? "").localeCompare(b.key ?? ""));
}

// ── The published entry gradient ────────────────────────────────────────────

/**
 * How a passport enters Saudi Arabia, as published. Ordered easiest → hardest;
 * `unverified` sits outside the ladder.
 *
 * The three-tier read this replaced (`novisa` / `onarrival` / `consular`) could
 * not express the thing that decides the policy ask, because it collapsed two
 * different states into `consular`: a market with NO route to a Saudi tourist
 * visa short of an embassy, and one whose travellers can reach an eVisa or a
 * visa on arrival by holding a qualifying document. Those markets want opposite
 * things — the first wants eligibility, the second wants the conditions relaxed
 * — and the old gradient printed the same word on both.
 *
 * It also split `novisa` from `onarrival` along the Passport Index label, a
 * distinction that says nothing about difficulty once a market is on Saudi's
 * official list: eVisa-online and visa-on-arrival are the same ask (none). The
 * ladder now steps on CONDITIONS, which is what a traveller actually meets.
 */
export type EntryCategory =
  /** No visa at all — GCC freedom of movement and the treaty exemptions. */
  | "novisa"
  /** On Saudi's official list: eVisa online or a visa on arrival, unconditionally. */
  | "evisa"
  /** Off the list, but a sourced route in exists for travellers who meet its conditions. */
  | "conditional"
  /** Off the list with no conditional route — a Saudi embassy or consulate, always. */
  | "consular"
  /** Entry refused in general. Outside the ladder: not a facilitation gap. */
  | "refused"
  /**
   * Passport Index asserts an eVisa/VoA route that Saudi's own list has not been
   * checked against. Never published on a market the overlay has run for — this
   * exists so an un-overlaid row states what it is instead of borrowing the
   * confidence of a tier it has not earned.
   */
  | "unverified";

export interface EntryCategoryMeta {
  key: EntryCategory;
  /** The tier's name, as the client's taxonomy words it. */
  label: string;
  /** A legend-width form of the same. */
  short: string;
  /** One sentence: what a traveller from such a market actually does. */
  desc: string;
}

/**
 * The gradient, in publication order, AS STORED — the copy carries `{country}`
 * and `{countryAdj}` placeholders for the focus country's short name and
 * adjective, and `entryCategories(profile)` below is what fills them in.
 *
 * Rendering these strings directly publishes the placeholder. That is
 * deliberate: this file used to name Saudi Arabia in four of them, and one
 * process serves two deployments, so a screen that forgot to resolve them would
 * have told a demo viewer their country's passports are checked against Saudi's
 * list. A visible `{country}` on the client is a bug someone fixes in a minute;
 * a plausible wrong country name on the demo is one nobody notices.
 * Every screen that grades entry — 2.3's
 * map and legend, GTM 4.2's state card, the printed brief, the assistant's
 * tool output — names the tiers from here, so a tier cannot be worded one way
 * on the map and another in the export.
 */
export const ENTRY_CATEGORIES: readonly EntryCategoryMeta[] = [
  {
    key: "novisa",
    label: "No visa",
    short: "No visa",
    desc: "Enters on a national ID or passport alone — no visa, no application, no fee.",
  },
  {
    key: "evisa",
    label: "eVisa or visa on arrival — no conditions",
    short: "eVisa / VoA",
    desc: "On {country}'s official eligible list: a tourist eVisa online before travel, or a visa on arrival. Nothing further to hold.",
  },
  {
    key: "conditional",
    label: "eVisa or visa on arrival — with conditions",
    short: "eVisa / VoA · conditional",
    desc: "Not on the official list. An eVisa or visa on arrival is open only to travellers who already hold a qualifying document — a used US, UK or Schengen visa, or US / UK / EU / GCC residence. Everyone else applies at a consulate.",
  },
  {
    key: "consular",
    label: "Consular only",
    short: "Consular only",
    desc: "Not on the official list and reached by no conditional route — every traveller applies in advance at a {countryAdj} embassy or consulate.",
  },
  {
    key: "refused",
    label: "Entry refused",
    short: "Entry refused",
    desc: "Entry and transit refused in general; narrow case-by-case exceptions only.",
  },
  {
    key: "unverified",
    label: "Not verified against {country}'s list",
    short: "Not verified",
    desc: "Passport Index reports an eVisa or visa-on-arrival route for this passport, which has not been checked against {country}'s own eligible list.",
  },
] as const;

const BY_KEY = new Map<string, EntryCategoryMeta>(ENTRY_CATEGORIES.map((c) => [c.key, c]));

/** Metadata for a published category, or null for a value outside the gradient. */
/**
 * The tier copy resolved for one deployment. Every screen that grades entry
 * renders from here.
 *
 * Note what is NOT templated: the `conditional` tier's sentence states which
 * documents open that route (a used US/UK/Schengen visa, GCC residence). Those
 * are the client's own policy, not a name — a deployment with a different
 * policy needs its own rules corpus, not a find-and-replace. Today the demo
 * loads no entry rules at all, so no market reaches that tier.
 */
export function entryCategories(profile: { shortName: string; adjective: string }): EntryCategoryMeta[] {
  const fill = (t: string) =>
    t.replaceAll("{country}", profile.shortName).replaceAll("{countryAdj}", profile.adjective);
  return ENTRY_CATEGORIES.map((c) => ({ ...c, label: fill(c.label), short: fill(c.short), desc: fill(c.desc) }));
}

/** The STORED meta for a key — placeholders intact. Callers rendering copy want
 *  `entryCategories(profile)`; this is for key/order lookups. */
export function entryCategoryMeta(key: string): EntryCategoryMeta | null {
  return BY_KEY.get(key) ?? null;
}

/**
 * The single place the published entry category is decided.
 *
 * Passport Index's bare "eVisa" label does NOT mean eVisa-eligible: every one of
 * the 104 markets it labels that way is absent from Saudi's official list and
 * must apply at an embassy. So the third-party label never decides the tier on
 * its own — `officialEligible` (imported as its own fact) does, and the raw
 * label is preserved separately for audit.
 *
 * What separates `conditional` from `consular` is whether a rule reaching this
 * passport is itself a route in. That is read off the imported rules rather than
 * asserted here: the predecessor of this function was a three-market IND/PAK/EGY
 * patch typed into GTM 4.2, which was both a spot-fix on 3 of 104 markets and
 * the reason the two sections disagreed.
 *
 * `officialEligible === null` means the overlay has not run for this row. The
 * eligibility-dependent tiers are then unreachable and the function says so
 * (`unverified`) rather than guessing in either direction — the hedge is the
 * honest answer, not a fallback.
 */
export function publishedCategory(
  rawCat: string,
  officialEligible: boolean | null | undefined,
  opts: { conditionalRoute: boolean },
): EntryCategory {
  if (rawCat === "refused") return "refused";
  // Visa-free needs no eligibility check: "no visa" is unambiguous from either
  // source, and the official list can only ever agree with it.
  if (rawCat === "novisa" && officialEligible !== false) return "novisa";
  if (officialEligible == null) return rawCat === "consular" ? "consular" : "unverified";
  if (officialEligible) return "evisa";
  return opts.conditionalRoute ? "conditional" : "consular";
}

/** The shape of a published market row, as much of it as the reader needs. */
export interface EntryCategoryCarrier {
  /** The published tier — on this gradient, or on the one that preceded it. */
  cat: string;
  /** The raw Passport Index category, when the payload carries it. */
  rawCat?: string | null;
  officialEligible?: boolean | null;
  /**
   * Present ONLY on a payload derived on this gradient. Its absence is the
   * version marker `entryCategoryOf` reads — see there.
   */
  conditionalRoute?: boolean;
}

/**
 * A market's tier, from a payload that may predate this gradient.
 *
 * The web is deployed separately from the server and routinely runs ahead of it
 * — a dev frontend pointed at an un-restarted backend, or the window between the
 * two deploys. A payload from before this gradient publishes the retired values
 * `onarrival` / a bare `evisa`, which match no tier, so the map found no colour
 * for them and rendered 68 countries — the whole official eligible list, the US,
 * the UK, the EU, Japan — as blank land. A legend that reads "eVisa / VoA · 0"
 * beside 68 uncoloured countries is worse than a stale answer: it looks like
 * data nobody has.
 *
 * `conditionalRoute` is the version marker rather than the shape of `cat`,
 * because `cat` alone is ambiguous — the old gradient published `evisa` for "the
 * eligibility overlay has not run", which is `unverified` here, not the eligible
 * tier of the same name. Only a server deriving on THIS gradient sends the
 * field, and when it does its answer is taken as final: re-deriving a category
 * the server already decided is how two screens end up grading one passport
 * twice, which is the defect this whole module exists to prevent.
 *
 * The upgrade path is not a rename table: the retired published values WERE the
 * raw Passport Index categories, passed through, so feeding them back to
 * `publishedCategory` reproduces the tier exactly — including the ambiguous
 * case, which resolves on `officialEligible` the same way a fresh derivation
 * would. A payload too old to carry eligibility at all lands on `unverified`,
 * which is what it is.
 */
export function entryCategoryOf(m: EntryCategoryCarrier, opts: { conditionalRoute: boolean }): EntryCategory {
  if (m.conditionalRoute !== undefined) return m.cat as EntryCategory;
  // Upgrade only where there is something to upgrade FROM. A row carrying a tier
  // and nothing else is a mock or a fixture, not a stale payload: re-deriving it
  // would read its missing eligibility as "not checked" and grade the whole
  // fallback map `unverified`.
  const hasContext = m.rawCat != null || m.officialEligible !== undefined;
  if (!hasContext && BY_KEY.has(m.cat)) return m.cat as EntryCategory;
  return publishedCategory(m.rawCat ?? m.cat, m.officialEligible, opts);
}

/**
 * Whether this tier is entry a traveller can complete without a consulate.
 *
 * `conditional` is deliberately NOT easy. It is a consular market with a door
 * for the minority of its travellers who already hold a used US / UK / Schengen
 * visa or Western residence — reading it as easy would republish exactly the
 * "entry is already easy, nothing to lobby for" verdict the eligibility overlay
 * was built to retract, on the same 121 markets.
 */
export function isEasyEntry(cat: string): boolean {
  return cat === "novisa" || cat === "evisa";
}
